"use client";

import Link from "next/link";
import type { Route } from "next";
import { motion } from "framer-motion";

import { trackSearchSubmitted, trackTrendingChipClicked } from "@/lib/analytics";
import { MOTION, STAGGER, ms } from "@/lib/design-tokens";
import { cn } from "@/lib/utils";
import type { TrendingItem } from "@/lib/api/trending";

interface Props {
  items: TrendingItem[];
}

/**
 * Shorten a stored product_label for display on a homepage chip.
 *
 * The full label (e.g. "Apple iPhone 17 Pro 256GB Black") is still
 * sent to /compare as the search query so retrieval precision is
 * preserved. Only the chip text is trimmed:
 *
 *   "Apple iPhone 17 Pro 256GB"           -> "iPhone 17 Pro"
 *   "Apple MacBook Air M5 13-inch"        -> "MacBook Air M5"
 *   "Google Pixel 10 Pro 256GB Black"     -> "Google Pixel 10 Pro"
 *   "Sony WH-1000XM5"                     -> "Sony WH-1000XM5" (unchanged)
 *   "Dyson V15 Detect"                    -> "Dyson V15 Detect" (unchanged)
 *
 * The cleanup rules:
 *   - Drop a redundant "Apple " prefix (iPhone IS Apple; MacBook IS
 *     Apple; restating the brand reads as filler).
 *   - Drop trailing storage variants (256GB, 512GB, 1TB, 2TB).
 *   - Drop trailing color variants when they appear as the last
 *     word (Black, White, Silver, Gold, etc.).
 *   - Drop trailing screen-size variants (13-inch, 14 inch, 15in).
 *
 * No other brand prefix is stripped. Sony / Dyson / Nintendo / Google
 * are part of the natural product name and stay.
 *
 * 2026-09-12 (owner: "cut item names, otherwise looks ugly"). With the
 * three "latest searched" chips now live, real long names reach the row,
 * e.g. "Philips SHC5000 Wireless TV Headphones with 30H Playtime
 * 4895229173149" (70 chars, ending in a barcode). Measured across the
 * 100 distinct names people have actually searched: median 26 chars,
 * p90 53, longest 78. Three more rules, applied in this order:
 *
 *   - Drop a trailing run of 8+ digits. That is a barcode / GTIN glued
 *     onto the name by a shop feed; no product is called that.
 *   - Drop a trailing " with ..." clause. It is a feature list, not the
 *     product's identity ("... Desk with LED Lights, Power Outlets, and
 *     Monitor Stand" -> "... Desk").
 *   - Cap at MAX_CHIP_CHARS on a word boundary with an ellipsis.
 *
 * Tested against all 100 real names: 33 shortened, 67 untouched, and
 * every one of the seven fixed chips (11-22 chars) untouched. 44 was
 * chosen over 40 because 40 clipped the product noun off several
 * appliance names ("... Front Load Washing" losing "Machine"); 44 keeps
 * "Samsung Neo QLED QN90F 4K Vision AI Smart TV" whole.
 *
 * Display only. The full label still drives the search / page link.
 */
const MAX_CHIP_CHARS = 44;

function cleanDisplayLabel(label: string): string {
  let result = label.trim();

  if (result.toLowerCase().startsWith("apple ")) {
    result = result.slice(6);
  }

  result = result.replace(/\s+\d{2,4}\s*(GB|TB)\b/gi, "");
  result = result.replace(
    /\s+(Black|White|Silver|Gold|Blue|Red|Green|Pink|Purple|Titanium|Graphite|Space\s+Grey|Space\s+Gray|Midnight|Starlight)$/i,
    "",
  );
  result = result.replace(/\s+\d{1,2}[\s-]?(inch|in)\b/gi, "");

  // A trailing barcode / GTIN is a shop-feed artefact, never a name.
  result = result.replace(/\s+\d{8,}$/, "");
  // A trailing feature clause is marketing, not identity.
  result = result.replace(/\s+with\s+.*$/i, "");
  result = result.replace(/[\s,-]+$/, "").trim();

  if (result.length > MAX_CHIP_CHARS) {
    const head = result.slice(0, MAX_CHIP_CHARS);
    const atWord = head.lastIndexOf(" ");
    result =
      (atWord > 0 ? head.slice(0, atWord) : head).replace(/[\s,-]+$/, "") +
      "\u2026";
  }

  return result.trim();
}

/**
 * Live trending searches under the homepage input.
 *
 * 2026-06-08 redesign (mockup C lines 102-119, signed off):
 *   - Label changed from "Trending right now" to "People are
 *     searching for" so the row reads as social proof rather than
 *     a generic feed.
 *   - Ghost TFP price chip removed from each item. The mockup shows
 *     only product labels; price discovery happens on /compare.
 *
 * Each chip is still a real product Mio recently searched. Click
 * navigates to /compare?q=<product_label> directly (these are
 * specific enough to skip clarification on the comparison route).
 *
 * Stagger preserved: each chip enters 50ms after the previous.
 */
export function TrendingSuggestions({ items }: Props) {
  if (items.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: ms(MOTION.base),
        ease: [0.16, 1, 0.3, 1],
        delay: 0.25,
      }}
      className="w-full"
      style={{ marginTop: 40 }}
    >
      <div
        className="text-center uppercase"
        style={{
          fontSize: "var(--fs-caption)",
          letterSpacing: "0.14em",
          marginBottom: 16,
          color: "hsl(var(--muted-foreground))",
        }}
      >
        People are searching for
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {items.map((item, idx) => (
          <TrendingChip key={item.product_label} item={item} index={idx} />
        ))}
        <BrowseChip index={items.length} />
      </div>
    </motion.div>
  );
}

/**
 * Browse entry at the end of the trending row (2026-08-28, owner design
 * pick: "same chip + arrow"). Identical pill anatomy/colors/hover-lift
 * to the product chips so it reads as the row's natural continuation;
 * the only tell is the arrow, which nudges right on hover. Staggers in
 * after the last product chip.
 */
function BrowseChip({ index }: { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        duration: ms(MOTION.smooth),
        ease: [0.16, 1, 0.3, 1],
        delay: 0.3 + (index * STAGGER.chips) / 1000,
      }}
    >
      <Link
        href={"/browse" as Route}
        onClick={() => {
          trackTrendingChipClicked({
            chipLabel: "browse_all",
            position: index,
          });
        }}
        className={cn(
          "group inline-flex items-center gap-1.5 rounded-full border transition-all duration-150",
          "border-border bg-secondary text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
          "hover:-translate-y-0.5 hover:border-[#D8D2C4] hover:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.10)]",
          "dark:border-white/[0.12] dark:bg-white/[0.04] dark:shadow-none",
          "dark:hover:border-white/20 dark:hover:bg-white/[0.07] dark:hover:shadow-none",
        )}
        style={{
          padding: "11px 18px",
          fontSize: "var(--fs-body)",
        }}
      >
        <span>Browse all</span>
        <span
          aria-hidden="true"
          className="transition-transform duration-150 group-hover:translate-x-0.5"
        >
          &rarr;
        </span>
      </Link>
    </motion.div>
  );
}

function TrendingChip({ item, index }: { item: TrendingItem; index: number }) {
  // Pinned chips carry a page_slug and go straight to the cached page
  // ($0, no sign-in wall); everything else searches via /compare.
  const href = (item.page_slug
    ? `/c/${item.page_slug}`
    : `/compare?q=${encodeURIComponent(item.product_label)}`) as Route;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        duration: ms(MOTION.smooth),
        ease: [0.16, 1, 0.3, 1],
        delay: 0.3 + (index * STAGGER.chips) / 1000,
      }}
    >
      <Link
        href={href}
        onClick={() => {
          // Fire BOTH events so GA's `search_submitted` count equals
          // the total number of search-initiating actions (typed +
          // chip click + search-again bar). `trending_chip_clicked`
          // stays so we can separately measure chip CTR vs typed.
          trackSearchSubmitted({
            query: item.product_label,
            source: "home",
          });
          trackTrendingChipClicked({
            chipLabel: cleanDisplayLabel(item.product_label),
            position: index,
          });
        }}
        className={cn(
          "group inline-flex items-center rounded-full border transition-all duration-150",
          // Light (Option A): white card + hairline border + soft shadow,
          // matching the search box / card system. Lifts on hover.
          "border-border bg-secondary text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
          "hover:-translate-y-0.5 hover:border-[#D8D2C4] hover:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.10)]",
          // Dark: subtle translucent fill + white hairline, no shadow (the old
          // white overlays were invisible on the LIGHT canvas - the bug).
          "dark:border-white/[0.12] dark:bg-white/[0.04] dark:shadow-none",
          "dark:hover:border-white/20 dark:hover:bg-white/[0.07] dark:hover:shadow-none",
        )}
        style={{
          padding: "11px 18px",
          fontSize: "var(--fs-body)",
        }}
      >
        <span>{cleanDisplayLabel(item.product_label)}</span>
      </Link>
    </motion.div>
  );
}
